import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

import {
  downloadFunctionalHistorySample,
  fetchFunctionalHistorySummary,
  generateSampleFunctionalHistory,
  HistoryImportResult,
  HistorySummary,
  importFunctionalHistory,
} from '../../api/history';
import { ApiError } from '../../utils/apiClient';
import { formatApiError } from '../../utils/formatApiError';
import { DOC_PATHS, TEMPLATE_PATHS } from '../../utils/onboardingResources';
import ActionButton from '../common/ActionButton';
import ResourceLink from '../common/ResourceLink';

type HistoryType = 'functional';

interface HistoricalDataTabProps {
  projectId: string;
  canEdit: boolean;
  focusType?: HistoryType | null;
  sampleOnlyMode?: boolean;
}

const formatTimestamp = (value?: string | null) => {
  if (!value) {
    return 'Not available';
  }

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const saveJsonFile = (filename: string, data: unknown) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const getImportErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof ApiError && error.body?.detail && typeof error.body.detail === 'object') {
    const detail = error.body.detail as Record<string, any>;
    const extraErrors = Array.isArray(detail.errors)
      ? detail.errors
          .slice(0, 3)
          .map((entry: any) => entry?.error)
          .filter(Boolean)
      : [];
    return [detail.detail || fallback, ...extraErrors].join(' ');
  }

  return formatApiError(error, fallback);
};

const HistorySummaryStats: React.FC<{ summary: HistorySummary | null }> = ({ summary }) => {
  if (!summary || summary.status === 'empty') {
    return (
      <Typography variant="body2" color="text.secondary">
        Not imported
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75}>
      <Typography variant="body2" color="text.secondary">
        {summary.records_imported} records imported
      </Typography>
      <Typography variant="body2" color="text.secondary">
        First execution: {formatTimestamp(summary.first_execution_at)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Latest execution: {formatTimestamp(summary.last_execution_at)}
      </Typography>
    </Stack>
  );
};

const HistorySectionCard: React.FC<{
  title: string;
  description: string;
  sampleFilename: string;
  summary: HistorySummary | null;
  result: HistoryImportResult | null;
  loading: boolean;
  error: string | null;
  canEdit: boolean;
  highlighted: boolean;
  sampleOnlyMode?: boolean;
  onUpload: (file: File) => Promise<void>;
  onDownloadSample: () => Promise<void>;
  onGenerateSample?: () => Promise<void>;
}> = ({
  title,
  description,
  sampleFilename,
  summary,
  result,
  loading,
  error,
  canEdit,
  highlighted,
  sampleOnlyMode,
  onUpload,
  onDownloadSample,
  onGenerateSample,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Card
      className="enterprise-card"
      sx={{
        borderRadius: 4,
        borderColor: highlighted
          ? 'rgba(57, 255, 20, 0.26)'
          : 'rgba(255,255,255,0.08)',
        boxShadow: highlighted ? '0 20px 42px rgba(57,255,20,0.08)' : undefined,
        scrollMarginTop: 96,
      }}
    >
      <CardContent sx={{ p: { xs: 2.25, md: 2.75 } }}>
        <Stack spacing={2.25}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.5}
          >
            <Box sx={{ maxWidth: 720 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.6 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {description}
              </Typography>
            </Box>
            <Chip
              label={
                error
                  ? 'Import failed'
                  : summary?.status === 'imported'
                  ? 'Imported'
                  : 'Not imported'
              }
              color={error ? 'warning' : summary?.status === 'imported' ? 'success' : 'default'}
              variant={summary?.status === 'imported' ? 'filled' : 'outlined'}
              sx={{
                bgcolor:
                  error
                    ? 'rgba(255,171,64,0.12)'
                    : summary?.status === 'imported'
                    ? 'rgba(57,255,20,0.14)'
                    : undefined,
              }}
            />
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack spacing={1.25}>
              <HistorySummaryStats summary={summary} />
              {result && (
                <Typography variant="body2" color="text.secondary">
                  Last import: {result.records_imported} imported, {result.records_rejected} rejected
                </Typography>
              )}
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <ActionButton
                startIcon={
                  loading ? (
                    <CircularProgress size={16} sx={{ color: '#04110A' }} />
                  ) : (
                    <FileUploadOutlinedIcon />
                  )
                }
                onClick={() => fileInputRef.current?.click()}
                disabled={!canEdit || loading || sampleOnlyMode}
              >
                Upload JSON
              </ActionButton>
              <ActionButton
                startIcon={<DownloadOutlinedIcon />}
                onClick={() => void onDownloadSample()}
                disabled={loading}
              >
                Download JSON template
              </ActionButton>
              {onGenerateSample && (
                <ActionButton
                  startIcon={<AutoAwesomeOutlinedIcon />}
                  onClick={() => void onGenerateSample()}
                  disabled={!canEdit || loading || sampleOnlyMode}
                >
                  Generate sample history
                </ActionButton>
              )}
            </Stack>
          </Stack>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={async (event) => {
              const file = event.target.files?.[0];
              if (file) {
                await onUpload(file);
              }
              event.target.value = '';
            }}
          />

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.48)' }}>
            Template file: {sampleFilename}
          </Typography>

          {error && (
            <Alert
              severity="warning"
              sx={{
                bgcolor: 'rgba(255,171,64,0.08)',
                border: '1px solid rgba(255,171,64,0.18)',
              }}
            >
              <Stack spacing={0.75}>
                <Typography variant="body2" fontWeight={700}>
                  The uploaded file could not be imported.
                </Typography>
                <Typography variant="body2">{error}</Typography>
                <ResourceLink
                  href={DOC_PATHS.dataUploadFormats}
                  target="_blank"
                  sx={{
                    alignSelf: 'flex-start',
                  }}
                >
                  View format guide
                </ResourceLink>
              </Stack>
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function HistoricalDataTab({
  projectId,
  canEdit,
  focusType,
  sampleOnlyMode = false,
}: HistoricalDataTabProps) {
  const [functionalSummary, setFunctionalSummary] = useState<HistorySummary | null>(null);
  const [functionalResult, setFunctionalResult] = useState<HistoryImportResult | null>(null);
  const [functionalError, setFunctionalError] = useState<string | null>(null);
  const [functionalLoading, setFunctionalLoading] = useState(false);
  const functionalRef = useRef<HTMLDivElement | null>(null);

  const refreshSummaries = async () => {
    const functional = await fetchFunctionalHistorySummary(projectId);
    setFunctionalSummary(functional);
  };

  useEffect(() => {
    void refreshSummaries();
  }, [projectId]);

  useEffect(() => {
    if (focusType === 'functional') {
      functionalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [focusType]);

  const bothEmpty = useMemo(
    () => functionalSummary?.status !== 'imported',
    [functionalSummary?.status]
  );

  const readJsonFile = async (file: File): Promise<unknown[]> => {
    const content = await file.text();
    const parsed = JSON.parse(content);
    if (!Array.isArray(parsed)) {
      throw new Error('The uploaded file must contain a JSON array.');
    }
    return parsed;
  };

  const handleFunctionalUpload = async (file: File) => {
    setFunctionalLoading(true);
    setFunctionalError(null);
    try {
      const records = await readJsonFile(file);
      const result = await importFunctionalHistory(projectId, records);
      setFunctionalResult(result);
      await refreshSummaries();
    } catch (error) {
      setFunctionalError(getImportErrorMessage(error, 'Functional history import failed.'));
    } finally {
      setFunctionalLoading(false);
    }
  };

  const handleGenerateFunctionalSample = async () => {
    setFunctionalLoading(true);
    setFunctionalError(null);
    try {
      await generateSampleFunctionalHistory(projectId);
      await refreshSummaries();
    } catch (error) {
        setFunctionalError(
          getImportErrorMessage(error, 'Sample execution history could not be generated.')
        );
    } finally {
      setFunctionalLoading(false);
    }
  };

  return (
    <Stack spacing={3.25}>
      <Stack spacing={1}>
        <Typography variant="h6" fontWeight={600}>
          Historical Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
          Upload functional execution history so Quantik Mind can learn from previous
          runs. Runtime observability can be connected separately, and history
          improves selection quality over time.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <ResourceLink
            href={DOC_PATHS.dataUploadFormats}
            target="_blank"
          >
            View format guide
          </ResourceLink>
          <ResourceLink
            href={TEMPLATE_PATHS.functionalHistory}
            target="_blank"
          >
            Download functional history template
          </ResourceLink>
        </Stack>
      </Stack>

      {sampleOnlyMode && (
        <Alert
          severity="info"
          sx={{
            bgcolor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          Sample projects use demo tests and sample execution history. Upload historical data after you switch to your own library so Quantik Mind can learn from real project signals.
        </Alert>
      )}

      {bothEmpty && (
        <Card
          className="enterprise-card"
          sx={{
            borderRadius: 4,
            background:
              'linear-gradient(135deg, rgba(18, 23, 31, 0.98) 0%, rgba(12, 16, 24, 0.98) 100%)',
            borderColor: 'rgba(255,255,255,0.08)',
          }}
        >
          <CardContent sx={{ p: { xs: 2.25, md: 2.75 } }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <InsightsOutlinedIcon sx={{ color: '#39FF14' }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  Start with one history source
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                Historical data helps Quantik Mind learn from previous runs. Functional history
                improves functional test selection, and observability can still be
                connected separately.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Box ref={functionalRef}>
        <HistorySectionCard
          title="Functional execution history"
          description="Upload previous functional test executions so Quantik Mind can learn which tests provide signal over time."
          sampleFilename="functional-history-sample.json"
          summary={functionalSummary}
          result={functionalResult}
          loading={functionalLoading}
          error={functionalError}
          canEdit={canEdit}
          sampleOnlyMode={sampleOnlyMode}
          highlighted={focusType === 'functional'}
          onUpload={handleFunctionalUpload}
          onDownloadSample={async () =>
            saveJsonFile(
              'functional-history-sample.json',
              await downloadFunctionalHistorySample()
            )
          }
          onGenerateSample={handleGenerateFunctionalSample}
        />
      </Box>

      {!canEdit && !sampleOnlyMode && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
          <Alert
            severity="info"
            sx={{
              bgcolor: 'rgba(0, 180, 255, 0.08)',
              border: '1px solid rgba(0, 180, 255, 0.18)',
            }}
          >
            You can view historical data status, but only project owners, organization admins,
            or internal admins can import new history.
          </Alert>
        </>
      )}
    </Stack>
  );
}
